/**
 * Brightness Control Utility for PWA
 * 
 * This utility provides screen brightness control capabilities for QR code display.
 * It uses the Wake Lock API to keep the screen on and investigates Screen Brightness API.
 * 
 * Browser Compatibility:
 * - Wake Lock API: Chrome 84+, Edge 84+, Safari 16.4+ (iOS 16.4+)
 * - Screen Brightness API: Currently NOT available in any browser (experimental/unavailable)
 * 
 * Fallbacks:
 * - For devices that don't support brightness control, we can optimize the UI
 * - Increase QR code size
 * - Use full-screen mode
 * - Add white background with high contrast
 * - Suggest users manually increase brightness
 */

/**
 * Safely access the screen global object
 * Helper to avoid repetitive eslint-disable comments
 */
function getScreenObject() {
  // eslint-disable-next-line no-restricted-globals
  return screen;
}

class BrightnessControl {
  constructor() {
    this.wakeLock = null;
    this.isWakeLockSupported = 'wakeLock' in navigator;
  }

  /**
   * Request to keep the screen awake (prevents screen dimming/sleeping)
   * This is crucial for QR code scanning as the scanner needs consistent visibility
   */
  async requestWakeLock() {
    if (!this.isWakeLockSupported) {
      console.warn('Wake Lock API is not supported in this browser');
      return false;
    }

    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
      console.log('Wake Lock activated - screen will stay on');

      // Re-acquire wake lock when page becomes visible again
      this.wakeLock.addEventListener('release', () => {
        console.log('Wake Lock released');
      });

      return true;
    } catch (err) {
      console.error(`Wake Lock error: ${err.name}, ${err.message}`);
      return false;
    }
  }

  /**
   * Release the wake lock (allow screen to sleep normally)
   */
  async releaseWakeLock() {
    if (this.wakeLock !== null) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('Wake Lock released manually');
      } catch (err) {
        console.error('Error releasing wake lock:', err);
      }
    }
  }

  /**
   * Check if Wake Lock is currently active
   */
  isWakeLockActive() {
    return this.wakeLock !== null && !this.wakeLock.released;
  }

  /**
   * Check for Screen Brightness API support
   * NOTE: As of 2024, this API is NOT available in any browser
   * This is for future compatibility when/if the API becomes available
   */
  checkBrightnessAPISupport() {
    // Check for experimental Screen Brightness API
    const screenObj = getScreenObject();
    const hasScreenBrightness = 'brightness' in screenObj;
    const hasGetScreenBrightness = 'getScreenBrightness' in navigator;
    
    return {
      supported: hasScreenBrightness || hasGetScreenBrightness,
      api: hasScreenBrightness ? 'screen.brightness' : 
           hasGetScreenBrightness ? 'navigator.getScreenBrightness' : 'none',
      message: hasScreenBrightness || hasGetScreenBrightness ? 
        'Screen Brightness API detected (experimental)' : 
        'Screen Brightness API not available - use UI optimizations instead'
    };
  }

  /**
   * Attempt to set screen brightness (experimental/unavailable in most browsers)
   * This will likely not work but is included for future compatibility
   */
  async setBrightness(level) {
    const support = this.checkBrightnessAPISupport();
    
    if (!support.supported) {
      console.warn('Screen Brightness API is not available');
      console.info('Recommendation: Ask users to manually increase brightness for QR scanning');
      return false;
    }

    try {
      // Experimental API (not currently available)
      const screenObj = getScreenObject();
      if ('brightness' in screenObj) {
        screenObj.brightness = level;
        return true;
      }
      
      if ('setScreenBrightness' in navigator) {
        await navigator.setScreenBrightness(level);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error setting brightness:', err);
      return false;
    }
  }

  /**
   * Get current screen brightness (experimental/unavailable)
   */
  async getBrightness() {
    const support = this.checkBrightnessAPISupport();
    
    if (!support.supported) {
      return null;
    }

    try {
      const screenObj = getScreenObject();
      if ('brightness' in screenObj) {
        return screenObj.brightness;
      }
      
      if ('getScreenBrightness' in navigator) {
        return await navigator.getScreenBrightness();
      }
      
      return null;
    } catch (err) {
      console.error('Error getting brightness:', err);
      return null;
    }
  }

  /**
   * Enter fullscreen mode for QR code display
   * This maximizes visibility and reduces distractions
   */
  async enterFullscreen(element = document.documentElement) {
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      }
      return true;
    } catch (err) {
      console.error('Error entering fullscreen:', err);
      return false;
    }
  }

  /**
   * Exit fullscreen mode
   */
  async exitFullscreen() {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
      return true;
    } catch (err) {
      console.error('Error exiting fullscreen:', err);
      return false;
    }
  }

  /**
   * Get a summary of available capabilities
   */
  getCapabilities() {
    const brightnessSupport = this.checkBrightnessAPISupport();
    
    return {
      wakeLock: {
        supported: this.isWakeLockSupported,
        active: this.isWakeLockActive(),
        description: 'Keeps screen on while displaying QR code'
      },
      brightness: {
        supported: brightnessSupport.supported,
        api: brightnessSupport.api,
        description: 'Controls screen brightness (currently unavailable in browsers)'
      },
      fullscreen: {
        supported: document.fullscreenEnabled || 
                   document.webkitFullscreenEnabled || 
                   document.mozFullScreenEnabled ||
                   document.msFullscreenEnabled,
        description: 'Displays QR code in fullscreen mode'
      }
    };
  }

  /**
   * Get user-friendly recommendations for optimal QR code display
   */
  getRecommendations() {
    const capabilities = this.getCapabilities();
    const recommendations = [];

    if (capabilities.wakeLock.supported) {
      recommendations.push({
        feature: 'Keep Screen On',
        status: 'available',
        action: 'Automatically activated when displaying QR code'
      });
    } else {
      recommendations.push({
        feature: 'Keep Screen On',
        status: 'unavailable',
        action: 'Adjust your device settings to prevent screen timeout'
      });
    }

    if (!capabilities.brightness.supported) {
      recommendations.push({
        feature: 'Screen Brightness',
        status: 'manual',
        action: 'Please increase your screen brightness manually for best QR scanning performance'
      });
    }

    if (capabilities.fullscreen.supported) {
      recommendations.push({
        feature: 'Fullscreen Mode',
        status: 'available',
        action: 'Tap the fullscreen button for optimal QR code display'
      });
    }

    return recommendations;
  }
}

// Factory function to create instances
export function createBrightnessControl() {
  return new BrightnessControl();
}

// Export singleton instance for convenience
let instance = null;
export const brightnessControl = {
  get instance() {
    if (!instance) {
      instance = new BrightnessControl();
    }
    return instance;
  }
};

// Export class for testing or multiple instances
export default BrightnessControl;
